import React, { useState, useEffect } from 'react';
import StableMuiDialog from '../../../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { WORKUP_REPORT_TYPES } from '../../../common/mappings.js';
import { usePersistedDataStore } from '../../store.js';
import { OFFER_DELIVERY_MODES, useOfferOptionsStore, workupOptionsFromDefaults } from './offerOptionsStore.js';

const WORKUP_TYPES_FOR_SEND_DIALOG = WORKUP_REPORT_TYPES.filter((r) => r.id !== 'loa' && !r.hideFromUi);
const WORKUP_TYPE_IDS_FOR_SEND_DIALOG = WORKUP_TYPES_FOR_SEND_DIALOG.map((r) => r.id);

/** Spreadsheet delivery options (embeddable or inside SendSpreadsheetOffersDialog). */
export const SpreadsheetOfferOptions = ({ defaultWorkupReportType = 'fnmaSummary' }) => {
  const defaults = useOfferOptionsStore((s) => ({
    includeCompsInOffersSpreadsheet: s.includeCompsInOffersSpreadsheet,
    workupDeliveryMode: s.workupDeliveryMode,
    workupReportType: s.workupReportType,
    setIncludeCompsInOffersSpreadsheet: s.setIncludeCompsInOffersSpreadsheet,
    setWorkupDeliveryMode: s.setWorkupDeliveryMode,
    setWorkupReportType: s.setWorkupReportType,
  }));
  const [workupDeliveryMode, setWorkupDeliveryMode] = useState(defaults.workupDeliveryMode);
  const initialWorkupType =
    WORKUP_TYPE_IDS_FOR_SEND_DIALOG.includes(defaults.workupReportType)
      ? defaults.workupReportType
      : (WORKUP_TYPE_IDS_FOR_SEND_DIALOG.includes(defaultWorkupReportType) ? defaultWorkupReportType : 'fnmaSummary');
  const [workupReportType, setWorkupReportType] = useState(initialWorkupType);
  const reportType = usePersistedDataStore((state) => state.negotiationObj?.reportType);

  useEffect(() => {
    const nextType = WORKUP_TYPE_IDS_FOR_SEND_DIALOG.includes(defaultWorkupReportType) ? defaultWorkupReportType : 'fnmaSummary';
    setWorkupDeliveryMode(defaults.workupDeliveryMode || OFFER_DELIVERY_MODES.OFFERS_ONLY);
    setWorkupReportType(
      WORKUP_TYPE_IDS_FOR_SEND_DIALOG.includes(defaults.workupReportType) ? defaults.workupReportType : nextType
    );
  }, [defaultWorkupReportType, defaults.workupDeliveryMode, defaults.workupReportType]);

  return (
    <>
      <FormControlLabel
        sx={{ mt: 1, display: 'block' }}
        control={
          <Checkbox
            checked={defaults.includeCompsInOffersSpreadsheet}
            onChange={(e) => {
              const on = e.target.checked;
              defaults.setIncludeCompsInOffersSpreadsheet(on);
              if (!on) setWorkupDeliveryMode(OFFER_DELIVERY_MODES.OFFERS_ONLY);
            }}
          />
        }
        label="Include comp addresses and workup value columns in spreadsheet"
      />
      <FormControl
        component="fieldset"
        sx={{ mt: 2 }}
        variant="standard"
        disabled={!defaults.includeCompsInOffersSpreadsheet}
      >
        <FormLabel component="legend">Workup delivery</FormLabel>
        <RadioGroup
          value={workupDeliveryMode}
          onChange={(e) => {
            const v = e.target.value;
            setWorkupDeliveryMode(v);
            defaults.setWorkupDeliveryMode(v);
          }}
        >
          <FormControlLabel value="offers" control={<Radio />} label="Offers only (no workup pdfs included)" />
          <FormControlLabel
            value="individual"
            control={<Radio />}
            label="Individual — each workup has its own link in offers spreadsheet"
          />
          <FormControlLabel value="batch" control={<Radio />} label="Batch — one combined PDF in email" />
        </RadioGroup>
      </FormControl>
      <FormControl
        size="small"
        fullWidth
        sx={{ mt: 2 }}
        disabled={
          !defaults.includeCompsInOffersSpreadsheet ||
          (workupDeliveryMode !== 'batch' && workupDeliveryMode !== 'individual')
        }
      >
        <InputLabel>Workup Type</InputLabel>
        <Select
          value={workupReportType}
          onChange={(e) => {
            const v = e.target.value;
            setWorkupReportType(v);
            defaults.setWorkupReportType(v);
          }}
          label="Workup Type"
        >
          {WORKUP_TYPES_FOR_SEND_DIALOG.map((r) => (
            <MenuItem
              key={r.id}
              value={r.id}
              disabled={reportType === 'assessment' && r.id === 'fnmaSummary'}
            >
              {r.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
};

export const getSpreadsheetConfirmPayload = (defaultWorkupReportType = 'fnmaSummary') => {
  const d = useOfferOptionsStore.getState();
  const workupReportType = WORKUP_TYPE_IDS_FOR_SEND_DIALOG.includes(d.workupReportType)
    ? d.workupReportType
    : (WORKUP_TYPE_IDS_FOR_SEND_DIALOG.includes(defaultWorkupReportType) ? defaultWorkupReportType : 'fnmaSummary');
  return {
    workupOptions: workupOptionsFromDefaults({
      includeCompsInOffersSpreadsheet: d.includeCompsInOffersSpreadsheet,
      workupDeliveryMode: d.workupDeliveryMode,
    }),
    workupReportType,
  };
};

/** Modal for spreadsheet offer delivery: comps-in-sheet, workup mode, workup report type. */
const SendSpreadsheetOffersDialog = ({ open, onClose, onConfirm, defaultWorkupReportType = 'fnmaSummary' }) => {
  const handleContinue = async () => {
    onClose();
    await onConfirm(getSpreadsheetConfirmPayload(defaultWorkupReportType));
  };

  return (
    <StableMuiDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Offers</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Configure your offer email options:
        </DialogContentText>
        {open && <SpreadsheetOfferOptions defaultWorkupReportType={defaultWorkupReportType} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        <Button onClick={handleContinue} variant="contained" color="primary">Continue</Button>
      </DialogActions>
    </StableMuiDialog>
  );
};

export default SendSpreadsheetOffersDialog;
